import { Injectable } from '@angular/core'
import {
  AccountSessionService,
  CLIENT_SESSION_ENDED_MESSAGE,
  isSessionGoneError,
  SIGNED_OUT_MESSAGE,
  SIGNED_OUT_MESSAGE_KEY,
} from '@employer/app/services/account-session.service'
import { IAuthService } from '@engineering11/auth-web'
import { NotificationHelper } from '@engineering11/web-ui-helpers'
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects'
import type { User } from 'firebase/auth'
import { from, switchMap, take, tap } from 'rxjs'
import { userActions } from 'shared-lib'

export const SIGNED_OUT_GRACE_MS = 1000
export const RELOAD_DELAY_MS = 1500

interface ITabIdentity {
  uid: string
  customerKey?: string
  switched: boolean
}

const SESSION_GONE = 'session-gone'
type IdentityRead = ITabIdentity | null | typeof SESSION_GONE | undefined

@Injectable()
export class AccountSessionEffects {
  private identity: ITabIdentity | null = null
  private reloading = false
  private unloading = false
  private signedOutTimer?: ReturnType<typeof setTimeout>

  constructor(
    private actions$: Actions,
    private authService: IAuthService,
    private accountSession: AccountSessionService,
    private notifications: NotificationHelper
  ) {
    window.addEventListener('beforeunload', () => (this.unloading = true))
  }

  watchAccount$ = createEffect(
    () =>
      this.authService.user$.pipe(
        switchMap(user => from(this.readIdentity(user))),
        tap(read => this.onIdentity(read))
      ),
    { dispatch: false }
  )

  noteLogOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userActions.OnLogOut),
        tap(() => this.accountSession.noteLoggingOut())
      ),
    { dispatch: false }
  )

  rememberSignedOutMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userActions.OnLogOutSuccess),
        tap(() => {
          const message = this.accountSession.pendingSignedOutMessage
          if (!message) return
          try {
            sessionStorage.setItem(SIGNED_OUT_MESSAGE_KEY, message)
          } catch {}
        })
      ),
    { dispatch: false }
  )

  showSignedOutMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROOT_EFFECTS_INIT),
        take(1),
        tap(() => {
          let message: string | null = null
          try {
            message = sessionStorage.getItem(SIGNED_OUT_MESSAGE_KEY)
            sessionStorage.removeItem(SIGNED_OUT_MESSAGE_KEY)
          } catch {}
          if (message) this.notifications.popNotification({ type: 'warning', title: 'Signed out', message })
        })
      ),
    { dispatch: false }
  )

  private async readIdentity(user: User | null): Promise<IdentityRead> {
    if (!user) return null
    try {
      const { claims } = await user.getIdTokenResult()
      return {
        uid: user.uid,
        customerKey: (claims['customerKey'] as string | undefined) ?? undefined,
        switched: claims['switchedFrom'] != null,
      }
    } catch (err) {
      return isSessionGoneError(err) ? SESSION_GONE : undefined
    }
  }

  private onIdentity(read: IdentityRead): void {
    if (this.reloading || read === undefined) return
    clearTimeout(this.signedOutTimer)

    if (read === SESSION_GONE) {
      this.endSession()
      return
    }

    if (read === null) {
      if (!this.identity || this.accountSession.isEndingSession) return
      // Our own sign-out paths (e.g. the disabled-account kick-out) navigate away straight after; let them.
      this.signedOutTimer = setTimeout(() => {
        if (!this.unloading) this.endSession()
      }, SIGNED_OUT_GRACE_MS)
      return
    }

    if (!this.identity) {
      // A tab's first sign-in (e.g. an act-as tab) settles any change this tab announced.
      this.accountSession.consumeExpectedAccountChange()
      this.identity = read
      return
    }
    if (!isDifferentAccount(this.identity, read) || this.accountSession.consumeExpectedAccountChange()) {
      this.identity = read
      return
    }

    this.reloading = true
    this.notifications.popNotification({
      type: 'warning',
      title: 'Account changed',
      message: 'Your account changed in another tab',
    })
    setTimeout(() => window.location.reload(), RELOAD_DELAY_MS)
  }

  private endSession(): void {
    const message = this.identity?.switched ? CLIENT_SESSION_ENDED_MESSAGE : SIGNED_OUT_MESSAGE
    this.identity = null
    this.accountSession.endSession(message)
  }
}

function isDifferentAccount(current: ITabIdentity, next: ITabIdentity): boolean {
  if (current.uid !== next.uid) return true
  return !!current.customerKey && !!next.customerKey && current.customerKey !== next.customerKey
}
